import { AvailableEntries } from './AvailableEntries';
import { SelectedEntries } from './SelectedEntries';
import { SearchInput } from '../SearchInput/SearchInput';
import { ActionButtons } from './ActionButtons';
import './MultiSelectWide.scss';
import type { Entry, EntryId } from './types';

type MultiSelectWideProps = {
    parentRoot: HTMLElement;
    multiple: boolean;
    availableEntries: Entry[];
    selectedIds: EntryId[];
    maxAllowedSelectedItems?: number; // 0 - unlimited
    minAllowedSelectedItems: number; // 0 - unlimited
    forceConvertIdToString?: boolean;
    initialQuery?: string;
    loadOptions?: (searchQuery: string) => Promise<Entry[]>; // Async function to load options
};

type MultiSelectWideCallbacks = {
    onChange: (selectedIds: EntryId[]) => void;
    onQueryChange?: (query: string) => void;
};

export class MultiSelectWide {
    private readonly root: HTMLElement;
    private readonly multiple: boolean;
    private availableEntries: Entry[];
    private readonly selectedIds: EntryId[];
    private readonly callbacks: MultiSelectWideCallbacks;
    private availableEntriesContainer!: AvailableEntries;
    private selectedEntriesContainer!: SelectedEntries;
    private searchInput!: SearchInput;
    private actionButtonsContainer!: ActionButtons;
    private enableAddAll: boolean;
    private maxAllowedSelectedItems: number;
    private forceConvertIdToString: boolean;
    private minAllowedSelectedItems: number;
    private initialQuery: string;
    private loadOptions?: (searchQuery: string) => Promise<Entry[]>;
    private isLoadingOptions: boolean = false;
    private loadOptionsDebounceTimer?: number;
    private pendingQuery: string | null = null;

    constructor(props: MultiSelectWideProps, callbacks: MultiSelectWideCallbacks) {
        this.root = props.parentRoot;
        this.multiple = props.multiple;
        this.availableEntries = props.availableEntries;
        this.selectedIds = props.selectedIds;
        this.forceConvertIdToString = props.forceConvertIdToString || false;
        // console.debug('MultiSelectWide.constructor() this.selectedIds', this.selectedIds);
        // console.debug('MultiSelectWide.constructor() this.selectedIds', this.availableEntries);
        this.callbacks = callbacks;
        this.enableAddAll = props.maxAllowedSelectedItems === undefined || false;
        this.maxAllowedSelectedItems = props.maxAllowedSelectedItems || 0;
        this.minAllowedSelectedItems = props.minAllowedSelectedItems || 0;
        this.initialQuery = props.initialQuery || '';
        this.loadOptions = props.loadOptions;

        if (this.forceConvertIdToString) {
            this.selectedIds = this.selectedIds.map(id => id.toString());
            this.availableEntries = this.availableEntries.map(entry => {
                return {
                    ...entry,
                    id: entry.id.toString(),
                };
            });
        }

        this.init();

        // If loadOptions is provided, load initial data
        if (this.loadOptions) {
            this.loadAndUpdateOptions(this.initialQuery, true);
        }
    }

    private init(): void {
        this.root.innerHTML = this.getHTML();

        this.availableEntriesContainer = new AvailableEntries(
            this.root.querySelector('.available-entries') as HTMLDivElement,
            {
                onClickEntry: (entry: Entry) => {
                    if (
                        this.maxAllowedSelectedItems > 0 &&
                        this.selectedEntriesContainer.getCount() >= this.maxAllowedSelectedItems
                    ) {
                        // this.actionButtonsContainer.showMaxAllowedSelectedItemsMessage();
                    } else {
                        // this.actionButtonsContainer.hideMaxAllowedSelectedItemsMessage();
                        this.selectedEntriesContainer.add(entry);
                        this.availableEntriesContainer.remove(entry);
                        this.availableEntriesContainer.redraw();
                        this.selectedEntriesContainer.redraw();
                        this.actionButtonsContainer.showSelectedCounter(
                            this.selectedEntriesContainer.getCount(),
                            this.maxAllowedSelectedItems
                        );
                        this.callbacks.onChange(
                            this.selectedEntriesContainer.getAll().map(entry => entry.id)
                        );
                    }
                },
            }
        );

        this.availableEntriesContainer.populate(
            this.availableEntries.filter(entry => this.selectedIds.indexOf(entry.id) === -1)
        );

        this.selectedEntriesContainer = new SelectedEntries(
            this.root.querySelector('.selected-entries') as HTMLDivElement,
            {
                onClickEntry: (entry: Entry) => {
                    if (this.selectedEntriesContainer.getCount() <= this.minAllowedSelectedItems) {
                        return false;
                    }
                    // console.debug('onClickEntry', entry);
                    this.availableEntriesContainer.add(entry);
                    this.selectedEntriesContainer.remove(entry);
                    this.availableEntriesContainer.redraw();
                    this.selectedEntriesContainer.redraw();
                    this.actionButtonsContainer.showSelectedCounter(
                        this.selectedEntriesContainer.getCount(),
                        this.maxAllowedSelectedItems
                    );
                    this.callbacks.onChange(
                        this.selectedEntriesContainer.getAll().map(entry => entry.id)
                    );
                },
            }
        );

        this.selectedEntriesContainer.populate(
            this.availableEntries.filter(entry => this.selectedIds.indexOf(entry.id) !== -1)
        );

        this.actionButtonsContainer = new ActionButtons(
            this.root.querySelector('.action-buttons') as HTMLDivElement,
            this.enableAddAll,
            {
                onAddAll: () => {
                    this.selectedEntriesContainer.populate([
                        ...this.selectedEntriesContainer.getAll(),
                        ...this.availableEntriesContainer.getAll(),
                    ]);
                    this.availableEntriesContainer.clear();
                    this.callbacks.onChange(
                        this.selectedEntriesContainer.getAll().map(entry => entry.id)
                    );
                },
            }
        );

        this.searchInput = new SearchInput(
            this.root.querySelector('.search-input') as HTMLDivElement,
            {
                onChange: (searchString: string) => {
                    // If loadOptions is provided, use async loading with debouncing
                    if (this.loadOptions) {
                        this.loadAndUpdateOptionsDebounced(searchString);
                    } else {
                        // Otherwise use local filtering (filter both available and selected entries)
                        this.availableEntriesContainer.filter(searchString);
                    }

                    // Always filter selected entries
                    this.selectedEntriesContainer.filter(searchString);

                    // Trigger onQueryChange callback if provided
                    if (this.callbacks.onQueryChange) {
                        this.callbacks.onQueryChange(searchString);
                    }
                },
            },
            {
                initialQuery: this.initialQuery,
            }
        );

        this.actionButtonsContainer.showSelectedCounter(
            this.selectedEntriesContainer.getCount(),
            this.maxAllowedSelectedItems
        );
    }

    private loadAndUpdateOptionsDebounced(query: string): void {
        // Store the latest query
        this.pendingQuery = query;

        // Clear previous debounce timer
        if (this.loadOptionsDebounceTimer) {
            window.clearTimeout(this.loadOptionsDebounceTimer);
        }

        // Show loading immediately
        this.availableEntriesContainer.showLoading();

        // Set new debounce timer (300ms delay)
        this.loadOptionsDebounceTimer = window.setTimeout(() => {
            this.loadAndUpdateOptions(query);
        }, 300);
    }

    private async loadAndUpdateOptions(
        query: string,
        isInitialLoad: boolean = false
    ): Promise<void> {
        if (!this.loadOptions) {
            return;
        }

        // If already loading, just store this query as pending
        if (this.isLoadingOptions) {
            this.pendingQuery = query;
            return;
        }

        this.isLoadingOptions = true;
        // Clear pending query as we're processing this one
        this.pendingQuery = null;

        // Show loading indicator (already shown by debounced version, but ensure it's there)
        this.availableEntriesContainer.showLoading();

        try {
            // Load new options from async function
            const newEntries = await this.loadOptions(query);

            // Convert IDs to string if needed
            let processedEntries = newEntries;
            if (this.forceConvertIdToString) {
                processedEntries = newEntries.map(entry => ({
                    ...entry,
                    id: entry.id.toString(),
                }));
            }

            // Check if all selectedIds are present in loaded data
            // If not, add missing entries as {id: id, name: id}
            const loadedIds = processedEntries.map(e => e.id);
            const missingSelectedIds = this.selectedIds.filter(id => !loadedIds.includes(id));

            if (missingSelectedIds.length > 0) {
                const missingEntries: Entry[] = missingSelectedIds.map(id => ({
                    id: id,
                    name: id.toString(),
                }));
                processedEntries = [...processedEntries, ...missingEntries];
            }

            // Update available entries
            this.availableEntries = processedEntries;

            // On initial load, populate selected entries if they exist in loaded data
            if (isInitialLoad && this.selectedIds.length > 0) {
                const initiallySelected = processedEntries.filter(entry =>
                    this.selectedIds.includes(entry.id)
                );
                if (initiallySelected.length > 0) {
                    this.selectedEntriesContainer.populate(initiallySelected);
                    this.selectedEntriesContainer.redraw();
                    this.actionButtonsContainer.showSelectedCounter(
                        this.selectedEntriesContainer.getCount(),
                        this.maxAllowedSelectedItems
                    );
                }
            }

            // Filter out entries that are already selected
            const availableOnly = processedEntries.filter(
                entry =>
                    !this.selectedEntriesContainer
                        .getAll()
                        .map(e => e.id)
                        .includes(entry.id)
            );

            // Update the available entries container with new data
            this.availableEntriesContainer.clear();
            this.availableEntriesContainer.populate(availableOnly);
            this.availableEntriesContainer.redraw();

            // Filter selected entries based on query
            this.selectedEntriesContainer.filter(query);

            // Show "no results" message if no entries found
            if (availableOnly.length === 0) {
                this.availableEntriesContainer.showNoResults();
            }
        } catch (error) {
            console.error('Error loading options:', error);
            // On error, show no results message
            this.availableEntriesContainer.showNoResults();
        } finally {
            this.isLoadingOptions = false;

            // If there's a pending query, load it now
            if (this.pendingQuery !== null) {
                const nextQuery = this.pendingQuery;
                this.pendingQuery = null;
                this.loadAndUpdateOptions(nextQuery);
            }
        }
    }

    private getHTML(): string {
        return `
            <div class="multi-select-wide-container">
                <div class="search-input"></div>
                <div class="action-buttons"></div>
                <div class="available-entries"></div>
                <div class="selected-entries"></div>
            </div>
        `;
    }
}
